import React, { useState } from 'react';
import { RefreshCw, Save, X, Edit2, AlertCircle, CheckCircle, DollarSign } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  useEmployeeRates, 
  useUpdateEmployeeRate,
  useSyncEmployees 
} from '../../../hooks/useEmployeeRates';
import { LoadingSpinner } from "../../common";

const EmployeeRatesSettings = () => {
  // Local state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [message, setMessage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // Default to 'active' only
  const [customRank, setCustomRank] = useState(''); // For adding new rank
  const [savingId, setSavingId] = useState(null); // Track which row is being saved
  const [successId, setSuccessId] = useState(null); // Track which row just succeeded
  const [refreshingId, setRefreshingId] = useState(null); // Track which row is being refreshed

  // Determine if we need to fetch inactive employees
  // Fetch all if showing 'all' or 'inactive', otherwise only fetch active
  const includeInactive = statusFilter === 'all' || statusFilter === 'inactive';
  
  // Use the hooks - pass includeInactive parameter
  const { employees, stats, loading, error, refetch } = useEmployeeRates(includeInactive);
  const updateMutation = useUpdateEmployeeRate();
  const syncMutation = useSyncEmployees();
  
  // Get query client for cache management
  const queryClient = useQueryClient();

  // Refresh data - clear cache and refetch
  const handleRefresh = async () => {
    try {
      // Clear all employee rates cache
      queryClient.removeQueries({ queryKey: ['employeeRates'] });
      
      // Refetch
      await refetch();
      showMessage('Employee list refreshed', 'success');
    } catch (error) {
      showMessage('Failed to refresh', 'error');
    }
  };

  // Refresh individual row
  const handleRefreshRow = async (qbEmployeeId, employeeName) => {
    try {
      setRefreshingId(qbEmployeeId);
      
      // Invalidate cache and refetch
      await queryClient.invalidateQueries({ 
        queryKey: ['employeeRates'],
        exact: false 
      });
      
      await refetch();
      
      setRefreshingId(null);
      showMessage(`${employeeName} refreshed`, 'success');
    } catch (error) {
      setRefreshingId(null);
      showMessage('Failed to refresh row', 'error');
    }
  };

  // Sync employees
  const handleSync = async () => {
    try {
      const result = await syncMutation.mutateAsync();
      showMessage(result.message || `Synced ${result.new_employees} new employees`, 'success');
    } catch (error) {
      showMessage('Sync failed', 'error');
    }
  };

  // Start editing
  const startEdit = (employee) => {
    setEditingId(employee.qb_employee_id);
    setEditForm({
      rate: employee.rate,
      rank: employee.rank || '',
      type: employee.type || 'FullTime',
      active_status: employee.active_status
    });
    setCustomRank('');
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
    setCustomRank('');
  };

  // Save changes
  const handleSave = async (qbEmployeeId) => {
    try {
      console.log('Starting save for:', qbEmployeeId);
      setSavingId(qbEmployeeId); // Mark row as saving
      
      // Determine final rank value
      let finalRank;
      
      if (editForm.rank === '__custom__') {
        // User selected "Add New Rank" - use the custom input
        if (!customRank.trim()) {
          showMessage('Please enter a rank value', 'error');
          setSavingId(null);
          return;
        }
        finalRank = customRank.trim();
      } else {
        // User selected from dropdown
        finalRank = editForm.rank || null;
      }
      
      await updateMutation.mutateAsync({
        qbEmployeeId,
        data: {
          rate: editForm.rate,
          rank: finalRank,
          type: editForm.type,
          active_status: editForm.active_status
        }
      });
      
      console.log('Save completed for:', qbEmployeeId);
      setSavingId(null);
      setSuccessId(qbEmployeeId); // Show success indicator
      
      showMessage('Employee updated successfully', 'success');
      cancelEdit();
      
      // Clear success indicator after 2 seconds
      setTimeout(() => {
        console.log('Clearing success indicator for:', qbEmployeeId);
        setSuccessId(null);
      }, 2000);
      
    } catch (error) {
      console.error('Save failed for:', qbEmployeeId, error);
      setSavingId(null);
      showMessage('Update failed: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  // Show message
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Filter employees
  const filteredEmployees = employees.filter(emp => {
    // Filter by type
    if (filter !== 'all' && emp.type?.toLowerCase() !== filter.toLowerCase()) {
      return false;
    }
    
    // Filter by status - using boolean active_status
    if (statusFilter === 'active' && emp.active_status !== true) {
      return false;
    }
    if (statusFilter === 'inactive' && emp.active_status !== false) {
      return false;
    }
    
    // Hide deleted employees
    if (emp.employee?.includes('(deleted)')) {
      return false;
    }
    
    return true;
  });

  // Get unique ranks from existing employees for suggestions
  const existingRanks = [...new Set(employees.map(e => e.rank).filter(Boolean))];
  const predefinedRanks = ['AAM', 'AM', 'Sr AM', 'Director', 'Freelance', 'Mktg Support'];
  const allRanks = [...new Set([...predefinedRanks, ...existingRanks])].sort();

  const employeeTypes = ['FullTime', 'PartTime', 'Freelance', 'Contractor'];

  if (loading) {
        return (
            // <div className="flex items-center justify-center h-64">
                <LoadingSpinner />
            // </div>
        );
    }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Employee Rates</h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage employee hourly rates, job ranks, and status
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 transition-colors"
              title="Refresh employee list"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleSync}
              disabled={syncMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              {syncMutation.isPending ? 'Syncing...' : 'Sync from QB'}
            </button>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`flex items-center gap-2 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Filters */}
      <div className="space-y-3">
        {/* Status Filter */}
        <div className="flex gap-2 items-center">
          <span className="text-sm font-medium text-gray-700">Filter:</span>
          <button
            onClick={() => setStatusFilter('active')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'active' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Active Only ({stats?.active || 0})
          </button>
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'all' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({stats?.total || 0})
          </button>
          <button
            onClick={() => setStatusFilter('inactive')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === 'inactive' 
                ? 'bg-gray-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Inactive Only ({stats?.inactive || 0})
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Rank</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Rate</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Last Updated</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredEmployees.map((employee) => {
                const isEditing = editingId === employee.qb_employee_id;
                const isSaving = savingId === employee.qb_employee_id;
                const isSuccess = successId === employee.qb_employee_id;
                const isRefreshing = refreshingId === employee.qb_employee_id;
                
                return (
                  <tr key={employee.qb_employee_id} className={`
                    hover:bg-gray-50 
                    ${!employee.active_status ? 'bg-gray-50 opacity-60' : ''}
                    ${isSaving ? 'bg-blue-50 animate-pulse' : ''}
                    ${isSuccess ? 'bg-green-50' : ''}
                    ${isRefreshing ? 'bg-yellow-50' : ''}
                    transition-colors duration-300
                  `}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{employee.employee}</div>
                        {employee.nickname && (
                          <div className="text-sm text-gray-500">{employee.nickname}</div>
                        )}
                      </div>
                    </td>
                    
                    {/* Status Column - Now editable */}
                    <td className="px-6 py-4">
                      {isEditing ? (
                        <select
                          value={editForm.active_status ? 'active' : 'inactive'}
                          onChange={(e) => setEditForm({...editForm, active_status: e.target.value === 'active'})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      ) : (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          employee.active_status 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {employee.active_status ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    
                    <td className="px-6 py-4">
                      {isEditing ? (
                        <select
                          value={editForm.type}
                          onChange={(e) => setEditForm({...editForm, type: e.target.value})}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {employeeTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          employee.type === 'FullTime' ? 'bg-green-100 text-green-800' :
                          employee.type === 'PartTime' ? 'bg-blue-100 text-blue-800' :
                          employee.type === 'Freelance' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {employee.type || 'Not Set'}
                        </span>
                      )}
                    </td>
                    
                    {/* Rank Column - Select existing or add new */}
                    <td className="px-6 py-4">
                      {isEditing ? (
                        <div className="space-y-2">
                          <select
                            value={editForm.rank === '__custom__' ? '__custom__' : editForm.rank || ''}
                            onChange={(e) => {
                              if (e.target.value === '__custom__') {
                                setEditForm({...editForm, rank: '__custom__'});
                                setCustomRank('');
                              } else {
                                setEditForm({...editForm, rank: e.target.value});
                                setCustomRank('');
                              }
                            }}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select Rank</option>
                            {allRanks.map(rank => (
                              <option key={rank} value={rank}>{rank}</option>
                            ))}
                            <option value="__custom__">+ Add New Rank</option>
                          </select>
                          
                          {editForm.rank === '__custom__' && (
                            <div>
                              <input
                                type="text"
                                placeholder="Enter new rank name"
                                value={customRank}
                                onChange={(e) => setCustomRank(e.target.value)}
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                              {customRank.trim() && (
                                <div className="text-xs text-green-600 mt-1">
                                  ✓ Will save as: "{customRank.trim()}"
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-700">{employee.rank || 'Not Set'}</span>
                      )}
                    </td>
                    
                    <td className="px-6 py-4">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          <input
                            type="number"
                            step="0.01"
                            value={editForm.rate}
                            onChange={(e) => setEditForm({...editForm, rate: parseFloat(e.target.value)})}
                            className="w-24 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-gray-500">/hr</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="font-semibold text-gray-900">
                            ${employee.rate.toFixed(2)}
                          </span>
                          <span className="text-gray-500 text-sm">/hr</span>
                        </div>
                      )}
                    </td>
                    
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {employee.date_updated 
                        ? new Date(employee.date_updated).toLocaleDateString()
                        : 'N/A'
                      }
                    </td>
                    
                    <td className="px-6 py-4 text-right">
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleSave(employee.qb_employee_id)}
                            disabled={updateMutation.isPending}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Save"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={updateMutation.isPending}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(employee)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredEmployees.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No employees found for this filter
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeRatesSettings;